import { useSyncExternalStore } from 'react'

/**
 * One audio player for the whole app (docs/UX-PLAYBOOK.md §5 "satu audio aktif global"): starting a clip stops the
 * previous one. A clip is a queue of URLs (a verse range plays verse after verse). Never autoplays.
 */

export type AudioState = {
  /** id of the clip that owns the player (null = idle) */
  id: string | null
  playing: boolean
  /** index of the file playing within the clip (which verse of a surah) */
  pos: number
  /** set when a clip fails to load or play; cleared by the next play */
  failed: boolean
}

let state: AudioState = { id: null, playing: false, pos: 0, failed: false }
let el: HTMLAudioElement | null = null
let queue: string[] = []

const listeners = new Set<() => void>()
const set = (next: Partial<AudioState>) => { state = { ...state, ...next }; listeners.forEach((l) => l()) }
const subscribe = (cb: () => void) => { listeners.add(cb); return () => { listeners.delete(cb) } }

function player() {
  if (el) return el
  el = new Audio()
  el.preload = 'none'
  el.addEventListener('ended', next)
  el.addEventListener('error', fail)
  el.addEventListener('pause', () => { if (state.playing && !el!.ended) set({ playing: false }) })
  el.addEventListener('play', () => set({ playing: true }))
  return el
}

function fail() {
  queue = []
  set({ id: null, playing: false, failed: true })
}

function start(src: string) {
  const a = player()
  a.src = src
  // play() returns a promise in browsers (rejects when blocked or the file cannot load).
  const p = a.play() as Promise<void> | undefined
  p?.catch?.((e: unknown) => { if ((e as Error)?.name !== 'AbortError') fail() })
}

function next() {
  const src = queue.shift()
  if (src) { set({ pos: state.pos + 1 }); start(src) }
  else set({ id: null, playing: false, pos: 0 })
}

/** Play/pause toggle for a clip. Same id → pause/resume; another id → stop the current one and start this one. */
export function toggleAudio(id: string, srcs: string[]) {
  if (state.id === id && el) {
    if (state.playing) { el.pause(); set({ playing: false }) }
    else {
      const p = el.play() as Promise<void> | undefined
      p?.catch?.(fail)
      set({ playing: true })
    }
    return
  }
  if (srcs.length === 0) return
  stopAudio()
  queue = srcs.slice(1)
  set({ id, playing: true, pos: 0, failed: false })
  start(srcs[0]!)
}

export function stopAudio() {
  queue = []
  if (el) { el.pause(); el.removeAttribute('src') }
  if (state.id !== null || state.playing) set({ id: null, playing: false, pos: 0 })
}

export const clearAudioError = () => { if (state.failed) set({ failed: false }) }

export const useAudio = () => useSyncExternalStore(subscribe, () => state)
